import { api, type MailView, type PlatformTenant, type PlatformUser, type User } from './api';
import { esc, fmtDate, openModal, qs, toast } from './ui';
import { billingSettingsDialog, paymentsDialog } from './billing';
import { t as $t, tk } from '@enercad/core';


/* Platform administration: companies, plans, subscription dates, suspension. */

const PLAN_KEYS = ['trial', 'basic', 'pro', 'enterprise'] as const;
const mb = (b: number) => (b < 1e6 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1e6).toFixed(1)} MB`);

export function platformView(root: HTMLElement, me: User, topbar: string, bindTop: () => void) {
  if (!me.platform_admin) {
    root.innerHTML = `<div class="shell">${topbar}<main class="list"><div class="empty">${$t("Bu sayfa yalnızca platform yöneticisi içindir.")}</div></main></div>`;
    return bindTop();
  }
  root.innerHTML = `<div class="shell">${topbar}<main class="list" style="max-width:1400px">
    <div class="list-head"><h1>${$t("Firmalar")}</h1><button class="btn" id="pays">${$t('Ödeme talepleri')} <span class="pill warn" id="paycount" hidden></span></button><button class="btn" id="prices" title="${$t('Plan fiyatları ve havale / EFT bilgileri')}">${$t('Fiyatlar')}</button><button class="btn" id="mail" title="${$t('Giden e-posta (SMTP) ayarları — yalnızca platform yöneticisi görür')}">${$t('E-posta')}</button><button class="btn pri" id="add">${$t("+ Firma")}</button></div>
    <p class="note" style="margin:-6px 0 12px">${$t("Ödemeler şimdilik elle takip edilir: ödemeyi aldığınızda planı ve \"bitiş\" tarihini güncelleyin. Süresi dolan firma verisini kaybetmez, projeleri salt okunur olur. Askıya alınan firma giriş yapamaz. Platform yöneticisi firmaların projelerini açamaz.")}</p>
    <div class="parts-tools"><input class="search" id="q" placeholder="${$t("Firma ya da yönetici e-postası ara…")}" autocomplete="off"><span class="note" id="sum" style="margin:0"></span></div>
    <div class="tblw"><table class="t"><thead><tr><th>${$t("Firma")}</th><th>${$t("Yönetici")}</th><th>${$t("Plan")}</th><th>${$t("Bitiş")}</th><th>${$t("Kullanıcı")}</th><th>${$t("Proje")}</th><th>${$t("Veri")}</th><th>${$t("Son işlem")}</th><th>${$t("Durum")}</th><th></th></tr></thead><tbody id="rows"></tbody></table></div>
  </main></div>`;
  bindTop();
  let tenants: PlatformTenant[] = [];
  root.querySelector('#mail')?.addEventListener('click', () => void mailDialog());
  root.querySelector('#prices')?.addEventListener('click', () => void billingSettingsDialog());
  root.querySelector('#pays')?.addEventListener('click', () => void paymentsDialog(() => void load()));

  const render = () => {
    const q = qs<HTMLInputElement>(root, '#q').value.trim().toLocaleLowerCase('tr');
    const list = tenants.filter((t) => !q || `${t.name} ${t.admin_email ?? ''}`.toLocaleLowerCase('tr').includes(q));
    const active = tenants.filter((t) => t.status === 'active' && !t.info.readOnly).length;
    qs(root, '#sum').textContent = $t("{0} firma · {1} etkin · {2} denemede", tenants.length, active, tenants.filter((t) => t.plan === 'trial').length);
    qs(root, '#rows').innerHTML = list
      .map((t) => {
        const i = tenants.indexOf(t), inf = t.info;
        const exp = t.plan === 'trial' ? t.trial_ends : t.paid_until;
        const left = inf.daysLeft == null ? '' : inf.daysLeft <= 0 ? `<span class="pill err">${$t("süresi doldu")}</span>` : inf.daysLeft <= 7 ? `<span class="pill warn">${$t("{0} gün", inf.daysLeft)}</span>` : `<span class="note">${$t("{0} gün", inf.daysLeft)}</span>`;
        const lim = (n: number, l: number | null) => `${n}${l != null ? ` / ${l}` : ''}`;
        return `<tr><td><b>${esc(t.name)}</b>${t.id === me.tenant_id ? ` <span class="note">${$t("(sizin)")}</span>` : ''}${t.notes ? `<br><span class="note">${esc(t.notes)}</span>` : ''}</td>
          <td class="m">${esc(t.admin_email ?? '—')}</td>
          <td><select data-plan="${i}" aria-label="${$t("Plan")}">${PLAN_KEYS.map((k) => `<option value="${k}"${t.plan === k ? ' selected' : ''}>${esc($t(PLAN_NAMES[k]))}</option>`).join('')}</select></td>
          <td style="white-space:nowrap"><input type="date" data-exp="${i}" value="${exp ?? ''}" aria-label="${$t("Bitiş tarihi")}"> ${left}</td>
          <td class="m">${lim(t.users, inf.limits.users)}</td><td class="m">${lim(t.projects, inf.limits.projects)}</td>
          <td class="m">${mb(t.bytes)}</td><td>${t.last_activity ? esc(fmtDate(t.last_activity)) : '—'}</td>
          <td>${t.status === 'suspended' ? `<span class="pill err">${$t("askıda")}</span>` : `<span class="pill ok">${$t("etkin")}</span>`}</td>
          <td style="white-space:nowrap"><button class="btn" data-users="${i}">${$t("Kullanıcılar")}</button> <button class="btn" data-edit="${i}">${$t("Düzenle")}</button>${t.id === me.tenant_id ? '' : ` <button class="btn${t.status === 'active' ? ' danger' : ''}" data-sus="${i}">${t.status === 'active' ? $t("Askıya al") : $t("Etkinleştir")}</button>`}</td></tr>`;
      })
      .join('') || `<tr><td colspan="10"><div class="empty">${$t("Firma yok.")}</div></td></tr>`;
  };
  const load = async () => {
    try {
      tenants = (await api.platformTenants()).tenants;
      render();
      const open = (await api.platformPayments()).requests.filter((r) => r.status === 'open').length;
      const badge = qs(root, '#paycount');
      badge.textContent = String(open);
      badge.hidden = !open;
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const save = async (t: PlatformTenant, body: Record<string, unknown>, msg: string) => {
    try {
      await api.platformPatch(t.id, body);
      toast(msg);
    } catch (e) {
      toast((e as Error).message);
    }
    load();
  };

  qs<HTMLInputElement>(root, '#q').oninput = render;
  root.addEventListener('change', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.dataset.plan != null) {
      const t = tenants[+el.dataset.plan];
      // moving off the trial: start a paid period of one year unless a date is already set
      const body: Record<string, unknown> = { plan: el.value };
      if (el.value !== 'trial' && !t.paid_until) body.paid_until = new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10);
      return void save(t, body, $t("{0}: plan {1}", t.name, $t(PLAN_NAMES[el.value as keyof typeof PLAN_NAMES])));
    }
    if (el.dataset.exp != null) {
      const t = tenants[+el.dataset.exp];
      return void save(t, t.plan === 'trial' ? { trial_ends: el.value || null } : { paid_until: el.value || null }, $t("{0}: bitiş {1}", t.name, el.value || 'yok'));
    }
  });
  root.addEventListener('click', (e) => {
    const el = e.target as HTMLElement;
    const sus = el.closest<HTMLElement>('[data-sus]')?.dataset.sus;
    if (sus != null) {
      const t = tenants[+sus];
      if (t.status === 'suspended') return void save(t, { status: 'active' }, $t("{0} etkinleştirildi", t.name));
      const close = openModal(
        `<h2>${$t("{0} askıya alınsın mı?", esc(t.name))}</h2><p>${$t("Firmanın tüm kullanıcılarının oturumu kapanır ve giriş yapamazlar. Veriler silinmez; istediğiniz zaman etkinleştirebilirsiniz.")}</p>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Askıya al")}</button></div>`,
        (a) => {
          if (a !== 'ok') return;
          close();
          void save(t, { status: 'suspended' }, $t("{0} askıya alındı", t.name));
        },
      );
      return;
    }
    const us = el.closest<HTMLElement>('[data-users]')?.dataset.users;
    if (us != null) return void usersDialog(tenants[+us]);
    const ed = el.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (ed != null) {
      const t = tenants[+ed];
      const f = (id: string, label: string, v: unknown, extra = '') => `<label class="fld"><span>${label}</span><input id="${id}" value="${esc(v ?? '')}" ${extra}></label>`;
      const close = openModal(
        `<h2>${esc(t.name)}</h2>
        ${f('tn', $t("Firma adı"), t.name)}
        <div class="row2">${f('tu', $t("Kullanıcı sınırı (boş = plan)"), t.max_users ?? '', 'inputmode="numeric"')}${f('tp', $t("Proje sınırı (boş = plan)"), t.max_projects ?? '', 'inputmode="numeric"')}</div>
        <div class="row2">${f('tt', $t("Deneme bitişi"), t.trial_ends ?? '', 'type="date"')}${f('tpu', $t("Ödeme bitişi"), t.paid_until ?? '', 'type="date"')}</div>
        <label class="fld"><span>${$t("Not (yalnızca siz görürsünüz)")}</span><textarea id="tno" rows="3" style="height:auto">${esc(t.notes ?? '')}</textarea></label>
        <dl class="kv"><dt>${$t("Oluşturma")}</dt><dd>${esc(fmtDate(t.created_at))}</dd><dt>${$t("Plan sınırları")}</dt><dd>${$t("{0}: {1} kullanıcı, {2} proje", esc($t(PLAN_NAMES[t.plan])), t.info.limits.users ?? '∞', t.info.limits.projects ?? '∞')}</dd></dl>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Kaydet")}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          const v = (id: string) => qs<HTMLInputElement>(dlg, '#' + id).value.trim();
          close();
          await save(t, { name: v('tn'), max_users: v('tu'), max_projects: v('tp'), trial_ends: v('tt') || null, paid_until: v('tpu') || null, notes: qs<HTMLTextAreaElement>(dlg, '#tno').value }, $t("Firma güncellendi"));
        },
      );
      return;
    }
    if (el.id === 'add') {
      const close = openModal(
        `<h2>${$t("Yeni firma")}</h2>
        <label class="fld"><span>${$t("Firma adı")}</span><input id="nc"></label>
        <div class="row2"><label class="fld"><span>${$t("Yönetici adı")}</span><input id="nn"></label><label class="fld"><span>${$t("Yönetici e-postası")}</span><input id="ne" type="email" autocapitalize="none"></label></div>
        <div class="row2"><label class="fld"><span>${$t("İlk şifre")}</span><input id="np" type="password" autocomplete="new-password"></label>
        <label class="fld"><span>${$t("Plan")}</span><select id="npl">${PLAN_KEYS.map((k) => `<option value="${k}">${esc($t(PLAN_NAMES[k]))}</option>`).join('')}</select></label></div>
        <label class="fld"><span>${$t("Ödeme bitişi (deneme dışı planlar)")}</span><input id="npu" type="date"></label>
        <div class="err" id="nerr"></div>
        <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Oluştur")}</button></div>`,
        async (a, dlg) => {
          if (a !== 'ok') return;
          const v = (id: string) => qs<HTMLInputElement>(dlg, '#' + id).value.trim();
          try {
            await api.platformCreate({ company: v('nc'), name: v('nn'), email: v('ne'), password: qs<HTMLInputElement>(dlg, '#np').value, plan: v('npl'), paid_until: v('npu') || null });
            close();
            toast($t("Firma oluşturuldu"));
            load();
          } catch (x) {
            qs(dlg, '#nerr').textContent = (x as Error).message;
          }
        },
      );
    }
  });
  load();
}

/** A readable random password without look-alike characters. */
const randomPassword = () => {
  const abc = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789', a = new Uint32Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (n) => abc[n % abc.length]).join('');
};

/** A company's accounts, each with a password reset. */
async function usersDialog(t: PlatformTenant) {
  let users: PlatformUser[];
  try {
    users = (await api.platformUsers(t.id)).users;
  } catch (e) {
    return void toast((e as Error).message);
  }
  const close = openModal(
    `<h2>${$t("{0} · kullanıcılar", esc(t.name))}</h2>
    <div class="tblw"><table class="t"><thead><tr><th>${$t("Ad")}</th><th>${$t("E-posta")}</th><th>${$t("Rol")}</th><th>${$t("Son giriş")}</th><th></th></tr></thead><tbody>${users
      .map(
        (u, i) => `<tr><td>${esc(u.name)}${u.platform_admin ? ` <span class="pill ok">${$t("platform")}</span>` : ''}${u.disabled ? ` <span class="pill err">${$t("devre dışı")}</span>` : ''}${u.email_verified ? '' : ` <span class="pill warn">${$t("e-posta doğrulanmadı")}</span>`}</td>
        <td class="m">${esc(u.email)}</td><td>${u.role === 'admin' ? $t("Yönetici") : $t("Kullanıcı")}</td>
        <td>${u.last_login ? esc(fmtDate(u.last_login)) : '—'}</td>
        <td style="white-space:nowrap">${u.email_verified ? '' : `<button class="btn" data-m="vf${i}" title="${$t("Müşteri doğrulama e-postasını alamıyorsa adresi elle onaylayın")}">${$t("Doğrulanmış say")}</button> `}<button class="btn" data-m="ed${i}">${$t("Düzenle")}</button> <button class="btn" data-m="pw${i}">${$t("Şifre sıfırla")}</button></td></tr>`,
      )
      .join('')}</tbody></table></div>
    <div class="acts"><button class="btn" data-m="close">${$t("Kapat")}</button></div>`,
    (a) => {
      const u = users[+a.slice(2)];
      if (!u) return;
      close();
      if (a.startsWith('vf'))
        void api.platformPatchUser(u.id, { email_verified: true }).then(() => (toast($t("E-posta doğrulanmış sayıldı")), usersDialog(t)), (e: Error) => toast(e.message));
      else if (a.startsWith('pw')) resetDialog(u);
      else editUserDialog(t, u);
    },
  );
}

/** Fix a mistyped name or e-mail; the e-mail is what the user signs in with. */
function editUserDialog(t: PlatformTenant, u: PlatformUser) {
  const close = openModal(
    `<h2>${esc(u.name)}</h2>
    <label class="fld"><span>${$t("Ad soyad")}</span><input id="en" value="${esc(u.name)}"></label>
    <label class="fld"><span>${$t("E-posta (giriş adı)")}</span><input id="ee" type="email" value="${esc(u.email)}" autocapitalize="none" spellcheck="false"></label>
    <p class="note">${$t("E-postayı değiştirirseniz kullanıcı bundan sonra yeni adresle giriş yapar; şifresi değişmez.")}</p>
    <div class="err" id="eerr"></div>
    <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn pri" data-m="ok">${$t("Kaydet")}</button></div>`,
    async (a, dlg) => {
      if (a !== 'ok') return;
      try {
        await api.platformPatchUser(u.id, { name: qs<HTMLInputElement>(dlg, '#en').value, email: qs<HTMLInputElement>(dlg, '#ee').value.trim().toLowerCase() });
        close();
        toast($t("Kullanıcı güncellendi"));
        void usersDialog(t);
      } catch (e) {
        qs(dlg, '#eerr').textContent = (e as Error).message;
      }
    },
  );
}

function resetDialog(u: PlatformUser) {
  const close = openModal(
    `<h2>${$t("{0} için yeni şifre", esc(u.name))}</h2>
    <p class="note">${$t("{0} · Kullanıcının açık oturumları kapanır; hesap devre dışıysa yeniden etkinleşir. Şifreyi kullanıcıya güvenli bir yoldan iletin; giriş yaptıktan sonra kendisi değiştirebilir.", esc(u.email))}</p>
    <label class="fld"><span>${$t("Yeni şifre")}</span><input id="rp" class="m" value="${randomPassword()}" autocomplete="off" spellcheck="false"></label>
    <div class="err" id="rerr"></div>
    <div class="acts"><button class="btn" data-m="close">${$t("Vazgeç")}</button><button class="btn" data-m="copy">${$t("Kopyala")}</button><button class="btn pri" data-m="ok">${$t("Şifreyi değiştir")}</button></div>`,
    async (a, dlg) => {
      const pw = qs<HTMLInputElement>(dlg, '#rp').value;
      if (a === 'copy') {
        await navigator.clipboard?.writeText(pw).catch(() => {});
        return void toast($t("Şifre kopyalandı"));
      }
      if (a !== 'ok') return;
      if (pw.length < 8) return void (qs(dlg, '#rerr').textContent = $t("Şifre en az 8 karakter olmalı."));
      try {
        await api.platformResetPassword(u.id, pw);
        close();
        toast($t("{0}: şifre değiştirildi", u.email));
      } catch (e) {
        qs(dlg, '#rerr').textContent = (e as Error).message;
      }
    },
  );
}

export const PLAN_NAMES = { trial: tk('Deneme'), basic: tk('Temel'), pro: tk('Profesyonel'), enterprise: tk('Kurumsal') };

/** Outgoing mail (SMTP) for verification links. Platform admin only. */
async function mailDialog() {
  let v: MailView;
  try {
    v = await api.mailSettings();
  } catch (e) {
    return toast((e as Error).message);
  }
  const s = v.settings;
  const src = v.source === 'env' ? $t('Şu an sunucunun ortam değişkenleri (SMTP_*) kullanılıyor; burada kaydettiğinizde bu ayarlar geçerli olur.') : v.source === 'none' ? $t('Henüz e-posta sunucusu yok: e-posta gönderilmiyor ve e-posta doğrulama kapalı.') : '';
  const close = openModal(
    `<h2>${$t('E-posta (SMTP)')}</h2>
    <p class="note">${$t('Kayıt olan firmalara doğrulama bağlantısı bu hesaptan gönderilir. Şifre sunucuda şifreli saklanır ve bir daha tarayıcıya gönderilmez; değiştirmek için yenisini yazın.')}</p>
    ${src ? `<p class="note">${src}</p>` : ''}
    <div class="row2"><label class="fld"><span>${$t('SMTP sunucusu')}</span><input id="mhost" value="${esc(s.host)}" placeholder="mail.firma.com" autocapitalize="none" spellcheck="false"></label>
    <label class="fld"><span>${$t('Bağlantı')}</span><select id="msec"><option value="587"${!s.secure ? ' selected' : ''}>587 · STARTTLS</option><option value="465"${s.secure ? ' selected' : ''}>465 · SSL</option></select></label></div>
    <div class="row2"><label class="fld"><span>${$t('Kullanıcı adı')}</span><input id="muser" value="${esc(s.user)}" placeholder="noreply@firma.com" autocomplete="off" autocapitalize="none" spellcheck="false"></label>
    <label class="fld"><span>${$t('Şifre')} ${v.passSet ? `<span class="pill ok">${$t('kayıtlı')}</span>` : `<span class="pill warn">${$t('yok')}</span>`}</span><input id="mpass" type="password" autocomplete="new-password" placeholder="${v.passSet ? $t('Değiştirmek için yeni şifreyi yazın') : ''}"></label></div>
    <label class="fld"><span>${$t('Gönderen')}</span><input id="mfrom" value="${esc(s.from)}" spellcheck="false"></label>
    <label class="fld"><span>${$t('Site adresi (e-postalardaki bağlantılar)')}</span><input id="murl" value="${esc(s.publicUrl || location.origin)}" spellcheck="false"></label>
    <label class="chkline"><input type="checkbox" id="mver"${s.verify ? ' checked' : ''}> ${$t('Kendi kendine kayıt olan firmalar e-postalarını doğrulamadan uygulamayı kullanamasın')}</label>
    <p class="note" id="mres"></p>
    <div class="acts"><button class="btn" data-m="close">${$t('Kapat')}</button><button class="btn" data-m="test">${$t('Kaydet ve deneme e-postası gönder')}</button><button class="btn pri" data-m="save">${$t('Kaydet')}</button></div>`,
    async (a, dlg) => {
      if (a !== 'save' && a !== 'test') return;
      const out = qs(dlg, '#mres');
      const port = Number(qs<HTMLSelectElement>(dlg, '#msec').value);
      const pass = qs<HTMLInputElement>(dlg, '#mpass').value;
      try {
        await api.saveMailSettings({
          host: qs<HTMLInputElement>(dlg, '#mhost').value.trim(),
          port,
          secure: port === 465,
          user: qs<HTMLInputElement>(dlg, '#muser').value.trim(),
          from: qs<HTMLInputElement>(dlg, '#mfrom').value.trim(),
          publicUrl: qs<HTMLInputElement>(dlg, '#murl').value.trim(),
          verify: qs<HTMLInputElement>(dlg, '#mver').checked,
          ...(pass ? { pass } : {}),
        });
      } catch (e) {
        return void (out.textContent = (e as Error).message);
      }
      if (a === 'save') {
        close();
        return void toast($t('E-posta ayarları kaydedildi'));
      }
      out.textContent = $t('Gönderiliyor…');
      try {
        const r = await api.testMail();
        out.innerHTML = r.ok
          ? `<span class="pill ok">${$t('Gönderildi')}</span> ${$t('{0} adresine gönderildi ({1} ms). Gelen kutunuzu kontrol edin.', esc(r.to ?? ''), r.ms)}`
          : `<span class="pill err">${$t('Hata')}</span> ${esc(r.msg ?? '')}`;
      } catch (e) {
        out.textContent = (e as Error).message;
      }
    },
  );
}
